import { BudgetItem, BudgetState, ClientCredit, StudioJob } from "./types";

// money maths shared by the comms stats and the budget. amounts on commissions are free text like "€120" or "80 usd",
// so everything goes through parseAmount first

const SYMBOLS: Array<[RegExp, string]> = [
  [/€|eur/i, "€"],
  [/£|gbp/i, "£"],
  [/¥|jpy|yen/i, "¥"],
  [/\$|usd|cad|aud/i, "$"]
];

// the money signs someone can pick in setup. auto reads the sign out of what they typed on their commissions
export const CURRENCIES = [
  { id: "auto", label: "auto", sign: "" },
  { id: "EUR", label: "€ euro", sign: "€" },
  { id: "USD", label: "$ dollar", sign: "$" },
  { id: "GBP", label: "£ pound", sign: "£" },
  { id: "JPY", label: "¥ yen", sign: "¥" },
  { id: "CAD", label: "C$ canadian", sign: "C$" },
  { id: "AUD", label: "A$ australian", sign: "A$" },
  { id: "CHF", label: "CHF swiss franc", sign: "CHF " }
];

// the picked sign, or the fallback when it's on auto
export function currencySign(id: string | undefined, fallback: string): string {
  return CURRENCIES.find((item) => item.id === id && item.sign)?.sign || fallback;
}

// the sign a commission is set to, or the fallback when it has none of its own
export function jobSign(job: StudioJob, fallback: string): string {
  return currencySign(job.currency, fallback);
}

// "€1.200,50", "1,200.50", "120 eur", "80" all become a plain number. nothing readable is 0
export function parseAmount(text: string | number): number {
  if (typeof text === "number") return Number.isFinite(text) ? text : 0;
  const found = String(text || "").replace(/\s/g, "").match(/-?\d[\d.,]*/);
  if (!found) return 0;
  let digits = found[0];
  const lastDot = digits.lastIndexOf(".");
  const lastComma = digits.lastIndexOf(",");
  // whichever comes last is the decimal mark, if two or fewer digits follow it
  const mark = Math.max(lastDot, lastComma);
  if (mark > 0 && digits.length - mark - 1 <= 2) {
    digits = `${digits.slice(0, mark).replace(/[.,]/g, "")}.${digits.slice(mark + 1)}`;
  } else {
    digits = digits.replace(/[.,]/g, "");
  }
  const value = Number(digits);
  return Number.isFinite(value) ? value : 0;
}

export function symbolOf(text: string): string {
  return SYMBOLS.find(([pattern]) => pattern.test(text))?.[1] || "";
}

// the sign most used in these amounts, € when nothing says
export function commonSymbol(texts: string[], fallback = "€"): string {
  const counts = new Map<string, number>();
  for (const text of texts) {
    const sign = symbolOf(text);
    if (sign) counts.set(sign, (counts.get(sign) || 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || fallback;
}

export function formatMoney(value: number, symbol = "€", short = false): string {
  const sign = value < 0 ? "-" : "";
  const size = Math.abs(value);
  if (short && size >= 10000) return `${sign}${symbol}${(size / 1000).toFixed(size >= 100000 ? 0 : 1).replace(/\.0$/, "")}k`;
  const digits = size % 1 && size < 1000 ? 2 : 0;
  return `${sign}${symbol}${size.toLocaleString("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

// "2026-10"
export function monthKey(date: string): string {
  return /^\d{4}-\d{2}/.test(date) ? date.slice(0, 7) : "";
}

export function todayKey(now = new Date()): string {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

// the last n months ending with this one, oldest first
export function lastMonths(count: number, now = new Date()): string[] {
  const months: string[] = [];
  for (let back = count - 1; back >= 0; back--) {
    const day = new Date(now.getFullYear(), now.getMonth() - back, 1);
    months.push(`${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}`);
  }
  return months;
}

export function monthLabel(month: string, withYear = false, locale = "en-GB"): string {
  const [year, number] = month.split("-").map(Number);
  const name = new Date(year, number - 1, 1).toLocaleString(locale, { month: "short" }).toLowerCase();
  return withYear ? `${name} ${String(year).slice(2)}` : name;
}

// ---------- commissions ----------

export function jobClient(job: StudioJob): string {
  return (job.client || job.name || "untitled").trim();
}

// the day a paid commission counts as money in
export function paidDay(job: StudioJob): string {
  return job.paidAt || job.due || job.createdAt || "";
}

// what the commission costs, and what has come in and is still to come
export function jobPrice(job: StudioJob): number {
  return parseAmount(job.amount);
}

export function jobPaid(job: StudioJob): number {
  return job.payments.reduce((total, payment) => total + payment.amount, 0);
}

export function jobOwed(job: StudioJob): number {
  return Math.max(0, jobPrice(job) - jobPaid(job));
}

export interface ClientBalance {
  client: string;
  owed: number;
  credit: number;
  open: number;
}

// per client: what they still owe on their commissions and the credit held for them. "Mira" and "mira" are one client
export function clientBalances(jobs: StudioJob[], credits: ClientCredit[]): ClientBalance[] {
  const rows = new Map<string, ClientBalance>();
  const row = (client: string) => {
    const key = client.toLowerCase();
    if (!rows.has(key)) rows.set(key, { client, owed: 0, credit: 0, open: 0 });
    return rows.get(key)!;
  };
  for (const job of jobs) {
    const entry = row(jobClient(job));
    const owed = jobOwed(job);
    entry.owed += owed;
    if (owed > 0) entry.open += 1;
  }
  for (const credit of credits) row(credit.client.trim() || "untitled").credit += credit.amount;
  return [...rows.values()].sort((a, b) => b.owed - a.owed || b.credit - a.credit || a.client.localeCompare(b.client));
}

export interface CommsStats {
  symbol: string;
  earnedMonth: number;
  earnedYear: number;
  earnedLastYear: number;
  earnedAll: number;
  // still owed on every commission that is not settled
  waiting: number;
  byMonth: Array<{ month: string; amount: number; count: number }>;
  byClient: Array<{ client: string; amount: number; count: number }>;
  byStatus: Array<{ status: string; amount: number; count: number }>;
}

// money counts on the day each payment came in, so a deposit and the balance can land in different months
export function commsStats(jobs: StudioJob[], statuses: string[], months = 12, now = new Date(), sign = ""): CommsStats {
  const symbol = sign || commonSymbol(jobs.map((job) => job.amount));
  const thisMonth = monthKey(todayKey(now));
  const thisYear = thisMonth.slice(0, 4);
  const payments = jobs.flatMap((job) => job.payments.map((payment) => ({ job, payment, day: payment.date || paidDay(job) })));
  const sum = (list: Array<{ payment: { amount: number } }>) => list.reduce((total, item) => total + item.payment.amount, 0);
  const jobsIn = (list: Array<{ job: StudioJob }>) => new Set(list.map((item) => item.job.id)).size;
  const byMonth = lastMonths(months, now).map((month) => {
    const inMonth = payments.filter((item) => monthKey(item.day) === month);
    return { month, amount: sum(inMonth), count: jobsIn(inMonth) };
  });
  const clients = new Map<string, { client: string; list: typeof payments }>();
  for (const item of payments) {
    const client = jobClient(item.job);
    const row = clients.get(client.toLowerCase()) || { client, list: [] };
    row.list.push(item);
    clients.set(client.toLowerCase(), row);
  }
  const byClient = [...clients.values()].map((row) => ({ client: row.client, amount: sum(row.list), count: jobsIn(row.list) })).sort((a, b) => b.amount - a.amount);
  const open = (list: StudioJob[]) => list.reduce((total, job) => total + jobPrice(job), 0);
  return {
    symbol,
    earnedMonth: sum(payments.filter((item) => monthKey(item.day) === thisMonth)),
    earnedYear: sum(payments.filter((item) => item.day.startsWith(thisYear))),
    earnedLastYear: sum(payments.filter((item) => item.day.startsWith(String(Number(thisYear) - 1)))),
    earnedAll: sum(payments),
    waiting: jobs.reduce((total, job) => total + jobOwed(job), 0),
    byMonth,
    byClient,
    byStatus: statuses.map((status) => {
      const list = jobs.filter((job) => job.status === status);
      return { status, amount: open(list), count: list.length };
    })
  };
}

// ---------- budget ----------

// does a monthly thing count in this month
function runsIn(item: BudgetItem, month: string): boolean {
  const start = monthKey(item.date) || monthKey(item.createdAt);
  const end = monthKey(item.until);
  return (!start || start <= month) && (!end || month <= end);
}

// what an income, bill or cost adds in one month
export function itemInMonth(item: BudgetItem, month: string): number {
  if (item.repeat === "monthly") return runsIn(item, month) ? item.amount : 0;
  return monthKey(item.date) === month ? item.amount : 0;
}

export function paidSoFar(item: BudgetItem): number {
  return item.payments.reduce((total, payment) => total + payment.amount, 0);
}

export function paymentsIn(item: BudgetItem, month: string): number {
  return item.payments.filter((payment) => monthKey(payment.date) === month).reduce((total, payment) => total + payment.amount, 0);
}

export interface MonthMoney {
  month: string;
  // income items plus paid commissions
  in: number;
  // bills, costs and debt payments
  out: number;
  // put toward goals
  saved: number;
}

export function monthMoney(state: BudgetState, month: string): MonthMoney {
  const items = state.items;
  const sum = (kinds: string[]) => items.filter((item) => kinds.includes(item.kind)).reduce((total, item) => total + itemInMonth(item, month), 0);
  const comms = state.comms.filter((job) => monthKey(job.date) === month).reduce((total, job) => total + job.amount, 0);
  const debtPaid = items.filter((item) => item.kind === "debt").reduce((total, item) => total + paymentsIn(item, month), 0);
  const saved = items.filter((item) => item.kind === "goal").reduce((total, item) => total + paymentsIn(item, month), 0);
  return { month, in: sum(["income"]) + comms, out: sum(["bill", "cost"]) + debtPaid, saved };
}

// monthly bills and costs still running, added up per category, biggest first
export function fixedByCategory(state: BudgetState, month: string): Array<{ category: string; amount: number }> {
  const totals = new Map<string, number>();
  for (const item of state.items) {
    if ((item.kind !== "bill" && item.kind !== "cost") || item.repeat !== "monthly" || !runsIn(item, month)) continue;
    const category = item.category.trim().toLowerCase() || "other";
    totals.set(category, (totals.get(category) || 0) + item.amount);
  }
  return [...totals.entries()].map(([category, amount]) => ({ category, amount })).sort((a, b) => b.amount - a.amount);
}
